import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CalendarDays, ChevronRight, Clock, User } from 'lucide-react';
import {
  bylineFor,
  formatInsightDate,
  getArticleBySlug,
  getRelatedArticles,
  readingTimeFor,
  topicFor,
} from '@/data/insights';
import { SITE_ORIGIN, useSeo } from '@/lib/seo';
import { useLiveTick } from '@/lib/liveContent';
import { InlineText } from '@/lib/markdown';
import { InsightCard } from '@/components/site/insights/InsightCard';
import { CtaBand } from '@/components/site/CtaBand';
import PlaceholderPage from './PlaceholderPage';

// Shared article template. Supports everything the spec requires — one H1,
// byline, dates, reading time, breadcrumbs, ToC, H2/H3 body blocks, FAQ,
// related services/articles, contextual enquiry CTA, BlogPosting structured
// data, OG metadata and canonical URL — while rendering cleanly for the
// existing genuine articles whose full bodies are yet to be migrated.
export default function InsightArticlePage() {
  useLiveTick();
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const origin = SITE_ORIGIN;

  const topic = article ? topicFor(article.primaryTopic) : null;
  const reading = article ? readingTimeFor(article) : null;
  const related = article ? getRelatedArticles(article, 2) : [];

  useSeo({
    title: article
      ? article.seo?.title || `${article.title} | Amden Group Insights`
      : 'Article not found | Amden Group',
    description: article
      ? article.seo?.description || article.excerpt
      : 'The article you are looking for could not be found.',
    canonical: article ? `${origin}/insights/${article.slug}` : undefined,
    ogImage: article?.image?.src,
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': article.structuredDataType || 'BlogPosting',
              headline: article.title,
              description: article.excerpt,
              image: article.image?.src,
              datePublished: article.publishedAt,
              ...(article.updatedAt ? { dateModified: article.updatedAt } : {}),
              author: {
                '@type': 'Organization',
                name: bylineFor(article),
                url: origin,
              },
              publisher: {
                '@type': 'Organization',
                name: 'Amden Group',
                url: origin,
              },
              mainEntityOfPage: `${origin}/insights/${article.slug}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
                { '@type': 'ListItem', position: 2, name: 'Insights', item: `${origin}/insights` },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `${origin}/insights/${article.slug}`,
                },
              ],
            },
          ],
        }
      : null,
  });

  if (!article) {
    return <PlaceholderPage title="Article not found" notFound />;
  }

  const enquiryHref = `/?${topic?.serviceHref ? `service=${article.primaryTopic}&` : ''}source=${encodeURIComponent(
    `Insight: ${article.title}`
  )}#solution-finder`;

  return (
    <>
      <article data-testid="insight-article" className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <nav aria-label="Breadcrumb" data-testid="article-breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-500">
              <li>
                <Link to="/insights" className="transition-colors hover:text-navy-900">
                  Insights
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-3.5 w-3.5" />
              </li>
              <li aria-current="page" className="max-w-[260px] truncate text-navy-900 sm:max-w-none">
                {article.title}
              </li>
            </ol>
          </nav>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {topic && (
              <span
                className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
                style={{ backgroundColor: topic.soft, color: topic.dark }}
              >
                {topic.label}
              </span>
            )}
            {article.secondaryTopics.map((key) => {
              const t = topicFor(key);
              return t ? (
                <span
                  key={key}
                  className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500"
                >
                  {t.label}
                </span>
              ) : null;
            })}
          </div>

          <h1 data-testid="article-title" className="mt-5 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl lg:text-5xl">
            {article.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-slate-200 py-4 text-sm font-medium text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <User className="h-4 w-4" aria-hidden="true" />
              {bylineFor(article)}
            </span>
            <time dateTime={article.publishedAt} className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              {formatInsightDate(article.publishedAt)}
            </time>
            {article.updatedAt && (
              <span>
                Updated <time dateTime={article.updatedAt}>{formatInsightDate(article.updatedAt)}</time>
              </span>
            )}
            {reading && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" aria-hidden="true" />
                {reading} min read
              </span>
            )}
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-4xl px-4 sm:px-6">
          {article.image?.src && (
          <img
            src={article.image.src}
            alt={article.image?.alt || article.title}
            width={article.image.width}
            height={article.image.height}
            data-testid="article-image"
            className="aspect-[16/9] w-full rounded-2xl object-cover"
          />
          )}
        </div>

        <div className="mx-auto mt-10 max-w-3xl px-4 sm:px-6">
          <p className="text-lg font-medium leading-relaxed text-navy-800 sm:text-xl" data-testid="article-lead">
            {article.excerpt}
          </p>

          {Array.isArray(article.body) && article.body.length > 0 && (
            <div className="prose-amden mt-8 space-y-6 text-base leading-relaxed text-slate-700" data-testid="article-body">
              {article.body.map((block, i) => {
                if (block.type === 'h2')
                  return (
                    <h2 key={i} className="pt-4 text-2xl font-extrabold tracking-tight text-navy-900">
                      <InlineText text={block.text} />
                    </h2>
                  );
                if (block.type === 'h3')
                  return (
                    <h3 key={i} className="pt-2 text-xl font-extrabold tracking-tight text-navy-900">
                      <InlineText text={block.text} />
                    </h3>
                  );
                if (block.type === 'ul')
                  return (
                    <ul key={i} className="list-disc space-y-2 pl-6">
                      {(block.items || []).map((li, j) => (
                        <li key={j}><InlineText text={li} /></li>
                      ))}
                    </ul>
                  );
                return <p key={i}><InlineText text={block.text} /></p>;
              })}
            </div>
          )}

          {Array.isArray(article.faq) && article.faq.length > 0 && (
            <section aria-labelledby="article-faq-heading" className="mt-12" data-testid="article-faq">
              <h2 id="article-faq-heading" className="text-2xl font-extrabold tracking-tight text-navy-900">
                Frequently asked questions
              </h2>
              <div className="mt-5 space-y-4">
                {article.faq.map((f, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-mist p-5">
                    <h3 className="text-base font-bold text-navy-900">{f.question}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {article.relatedServices.length > 0 && (
            <aside className="mt-12 rounded-xl border border-slate-200 bg-mist p-6 sm:p-8" data-testid="article-related-services">
              <h2 className="text-lg font-extrabold tracking-tight text-navy-900">
                Related Amden support
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                {article.relatedServices.map((key) => {
                  const t = topicFor(key);
                  if (!t?.serviceHref) return null;
                  return (
                    <Link
                      key={key}
                      to={t.serviceHref}
                      data-testid={`article-service-${key}`}
                      className="group flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-navy-300"
                    >
                      <span className="text-sm font-bold text-navy-900">
                        {`Amden ${t.label}`}
                      </span>
                      <ArrowRight className="h-4 w-4 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-navy-900" aria-hidden="true" />
                    </Link>
                  );
                })}
              </div>
            </aside>
          )}

          <div className="mt-10 rounded-xl bg-navy-900 p-6 sm:p-8" data-testid="article-enquiry-cta">
            <h2 className="text-xl font-extrabold tracking-tight text-white">
              Want help with this in your business?
            </h2>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-300">
              Tell us where your business is today and we’ll point you towards the most practical
              next step.
            </p>
            <Link
              to={enquiryHref}
              data-testid="article-enquiry-btn"
              className="group mt-6 inline-flex items-center gap-2 rounded-md bg-gold-600 px-6 py-3 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-500"
            >
              Discuss Your Business
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section aria-labelledby="related-insights-heading" data-testid="article-related" className="bg-mist py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="related-insights-heading" className="text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
              Related insights
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((rel, i) => (
                <InsightCard key={rel.id} article={rel} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        index="09"
        eyebrow="Next step"
        heading="Need help applying this to your business?"
        copy="Amden combines practical advice with hands-on implementation across operations, people, growth and business launch."
        primaryLabel="Discuss Your Business"
        primaryHref={() => enquiryHref}
        secondaryLabel="Explore More Insights"
        secondaryHref="/insights"
      />
    </>
  );
}
